import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, MenuItem, Select, TextField, Typography, styled } from '@mui/material';
import Progress from '../../progress/Progress';
import { buildingRelatedForms, DEFAULT_FORM, EFormType, EFormTypeKey, IForm, propertyGroupRelatedForms } from '../../../models/form/form';
import { ELanguage } from '../../../models/enums/language';
import { availableLanguages, eLanguages } from '../../../i18n';
import objectUtils from '../../../utils/objectUtils';
import BuildingService from '../../../services/building/BuildingService';
import FormService from '../../../services/form/FormService';
import IAdminPageBuildingDetail from '../../../models/adminPage/adminPageBuildingDetail';
import ModalCard, { ModalRow, ModalTitle } from '../../modal/ModalCard';
import MultiFreeInput from '../../input/form/MultiFreeInput';
import { validEmailRegex } from '../../../utils/regex';
import AuthService from "../../../services/auth/AuthService";
import { IPropertyGroup } from '../../../models/propertyGroup';
import propertyGroupService from '../../../services/propertyGroupService/PropertyGroupService';

const ErrorMessage = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    textAlign: 'center',
    marginBottom: 16
}));

interface IAddNewFormModalProps {
    isShowCreate: boolean;
    setIsShowCreate: (v: boolean) => void;
    setUpdateForms: Dispatch<SetStateAction<number>>;
}


const AddNewFormModal = ({ isShowCreate, setIsShowCreate, setUpdateForms }: IAddNewFormModalProps) => {
    const { t } = useTranslation(['form', 'common']);
    const [isFormSending, setIsFormSending] = useState(false);
    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
    const [form, setForm] = useState<IForm>(DEFAULT_FORM);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        BuildingService.GetBuildingsDetails().then((buildings) => setBuildings(buildings));
        propertyGroupService.GetPropertyGroups().then((propertyGroups) => setPropertyGroups(propertyGroups));
    }, []);

    const onAddForm = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsFormSending(true);

        FormService.PostAllFormTemplate(form)
            .then(() => setUpdateForms((old) => old + 1))
            .then(() => { closeModal() })
            .catch((error) => setErrorMessage("form:error.formSentError"))
            .finally(() => setIsFormSending(false));
    };

    const closeModal = () => {
        setIsShowCreate(false);
        setIsFormSending(false);
        setForm(DEFAULT_FORM);
    };

    const updateEmails = (emails: string[]) => {
        if (form.emails.length < emails.length) {
            const newEmail = emails[emails.length - 1];

            if (!validEmailRegex.test(newEmail)) {
                setErrorMessage("form:error.emailInvalid");
                return false;
            }
        }

        setForm((old) => old && { ...old, emails });
        return true;
    }

    const isFormValid = () => {
        if (!form.name || !form.formType || form.formType === EFormType.Undefined || !form.lang) {
            return false;
        }

        if (form.emails.some((email) => !validEmailRegex.test(email))) {
            return false;
        }

        return true;
    }

    return (
        <Dialog fullWidth PaperProps={{ style: { minWidth: '60vw' } }} open={isShowCreate} onClose={closeModal}>
            <ModalCard sx={{ borderRadius: 4 }} title={t('form:createForm')} closeCallback={closeModal}>
                <form onSubmit={onAddForm}>
                    <ModalCard.Main isDivider={false}>
                        <ModalRow>
                            <ModalTitle>{t('form:formName')}*</ModalTitle>
                            <TextField
                                required
                                style={{ textAlign: 'left', width: '100%' }}
                                value={form.name}
                                onChange={(e) => {
                                    setForm((old) => ({ ...old, name: e.target.value }));
                                }}
                            />
                        </ModalRow>

                        <ModalRow>
                            <ModalTitle>{t('form:description')}</ModalTitle>
                            <TextField
                                style={{ textAlign: 'left', width: '100%' }}
                                value={form.description}
                                onChange={(e) => {
                                    setForm((old) => ({ ...old, description: e.target.value }));
                                }}
                            />
                        </ModalRow>

                        <ModalRow>
                            <ModalTitle>{t('form:formType')}*</ModalTitle>
                            <Select
                                value={form.formType !== EFormType.Undefined ? form.formType : undefined}
                                onChange={(e) => setForm((old) => ({ ...old, formType: e.target.value as EFormType, propertyGroups: [], buildings: [] }))}
                                sx={{ width: '100%', height: '2em' }}
                                MenuProps={{ style: { overflowY: 'visible' } }}
                                required
                            >
                                {objectUtils
                                    .enumNumberKeys(EFormType)
                                    .filter((type) => (type as EFormType) != EFormType.Undefined)
                                    .map((formType) => (
                                        <MenuItem key={formType} value={formType}>
                                            {t(`form:type.${EFormType[formType] as EFormTypeKey}`)}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </ModalRow>

                        {buildingRelatedForms.includes(form.formType) && (

                            <ModalRow>
                                <ModalTitle>{t('form:buildings')}*</ModalTitle>
                                <Select
                                    multiple
                                    required
                                    value={form.buildings.map((b) => b.buildingId)}
                                    onChange={(e) => setForm((old) => old && {
                                        ...old, buildings: (e.target.value as number[]).map((buildingId) => ({
                                            id: 0,
                                            formId: old.id,
                                            buildingId: buildingId,
                                            isActive: old.buildings.find((building) => building.buildingId == buildingId)?.isActive || false
                                        }))
                                    })}
                                    sx={{ width: '100%', height: '2em' }}
                                    MenuProps={{ style: { overflowY: 'visible' } }}
                                >
                                    {buildings.map((building) => (
                                        <MenuItem
                                            key={`building_${building.id}`}
                                            value={building.id}
                                        >
                                            {building.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </ModalRow>
                        )}

                        {propertyGroupRelatedForms.includes(form.formType) && (
                            <ModalRow>
                                <ModalTitle>{t('form:propertyGroups')}*</ModalTitle>
                                <Select
                                    multiple
                                    required
                                    value={form.propertyGroups.map((pg) => pg.propertyGroupId)}
                                    onChange={(e) => setForm((old) => old && {
                                        ...old, propertyGroups: (e.target.value as number[]).map((propertyGroupId) => ({
                                            id: 0,
                                            formId: old.id,
                                            propertyGroupId: propertyGroupId,
                                            isActive: old.propertyGroups.find((propertyGroup) => propertyGroup.propertyGroupId == propertyGroupId)?.isActive || false
                                        }))
                                    })}
                                    sx={{ width: '100%', height: '2em' }}
                                    MenuProps={{ style: { overflowY: 'visible' } }}
                                >
                                    {propertyGroups.map((propertyGroup) => (
                                        <MenuItem
                                            key={`propertyGroup_${propertyGroup.id}`}
                                            value={propertyGroup.id}
                                        >
                                            {propertyGroup.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </ModalRow>
                        )}


                        <ModalRow>
                            <ModalTitle>{t('form:language')}*</ModalTitle>
                            <Select
                                required
                                value={form.lang}
                                onChange={(e) => setForm((old) => ({ ...old, lang: e.target.value as ELanguage }))}
                                sx={{ width: '100%', height: '2em' }}
                                MenuProps={{ style: { overflowY: 'visible' } }}
                            >
                                {availableLanguages.map((lang) => (
                                    <MenuItem key={lang} value={eLanguages.get(lang)}>
                                        {t(`common:header.${ELanguage[eLanguages.get(lang) as ELanguage].toLowerCase()}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </ModalRow>

                        <ModalRow>
                            <ModalTitle>{t('form:emails')}</ModalTitle>
                            <MultiFreeInput
                                width={'100%'}
                                valueArray={form.emails || []}
                                setValueArray={(valueArray) => {
                                    setErrorMessage("")
                                    return updateEmails(valueArray);
                                }}
                                setErrorMessage={(errorMessage) => setErrorMessage(errorMessage)}
                            />
                        </ModalRow>
                        {errorMessage && <ErrorMessage>{t(errorMessage)}</ErrorMessage>}

                    </ModalCard.Main>
                    <ModalCard.Action>
                        <Progress isLoading={isFormSending}>
                            <Button type="submit" variant="contained" color="secondary" disabled={!isFormValid()}>
                                <ModalTitle>{t('form:create')}</ModalTitle>
                            </Button>
                        </Progress>
                    </ModalCard.Action>
                </form>
            </ModalCard>
        </Dialog>
    );
};

export default AddNewFormModal;
